import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Button from "@/components/button/Button";
import CalendarModalShell from "@/components/modals/CalendarModalShell";
import ConfirmModal from "@/components/modals/ConfirmModal";
import type { ShellDayCell } from "@/components/modals/CalendarModalShell.types";
import NeedsExceptionDayCell, { type NeedsDayCellChange } from "./NeedsExceptionDayCell";
import type { RoleInfo } from "./roleInterfaces";
import type { SpecificNeedsConstraints } from "@/types/needsContrains";
import {
    buildSpecDaysPayload,
    equalsDefaultOnEditable,
    getDayOfWeekDefault,
    getEffectiveConfig,
} from "./needsExceptionUtils";
import { updateExtendedWgData } from "@/api.ts";
import { NEED_KEY_LABELS } from "./needKeys";

interface NeedsExceptionCalendarModalProps {
    open: boolean;
    onClose: () => void;
    role: RoleInfo | null;
    startDate: string;
    endDate: string;
    holidays?: string[];
    onPrev?: () => void;
    onNext?: () => void;
    hasPrev?: boolean;
    hasNext?: boolean;
    onSaved?: () => void;
}

export default function NeedsExceptionCalendarModal(
    {
        open,
        onClose,
        role,
        startDate,
        endDate,
        holidays,
        onPrev,
        onNext,
        hasPrev,
        hasNext,
        onSaved,
    }: Readonly<NeedsExceptionCalendarModalProps>) {
    const [draft, setDraft] = useState<Record<string, SpecificNeedsConstraints>>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [committedJson, setCommittedJson] = useState("{}");

    const hasUnsavedChanges = !!role && JSON.stringify(draft) !== committedJson;

    // Each visit of a row gets its own generation, so a save still in flight when the user
    // navigates (even back to the same row) cannot update the state of a fresh visit.
    const visitRef = useRef(0);

    // Layout effect: the reset must land before paint, or the previous row's draft shows for a frame.
    useLayoutEffect(() => {
        if (open) {
            visitRef.current += 1;
            setDraft({});
            setCommittedJson("{}");
            setError(false);
            setJustSaved(false);
            setSaving(false);
            setPendingAction(null);
        }
    }, [role?.rowKey, open]);

    useEffect(() => {
        if (!justSaved) {
            return;
        }

        const timer = setTimeout(() => setJustSaved(false), 3000);
        return () => clearTimeout(timer);
    }, [justSaved]);

    const guarded = (action?: () => void) => () => {
        if (!action) {
            return;
        }

        if (hasUnsavedChanges) {
            setPendingAction(() => action);
        } else {
            action();
        }
    };

    const handleDayCellChange = useCallback<NeedsDayCellChange>((dateStr, patch) => {
        if (!role) {
            return;
        }

        setJustSaved(false);
        setDraft((prev) => {
            const base = prev[dateStr] ?? getEffectiveConfig(role, dateStr);
            return {
                ...prev,
                [dateStr]: { ...base, ...patch, active: "true" },
            };
        });
    }, [role]);

    const renderDayCell = useCallback((cell: ShellDayCell) => {
        if (!role) {
            return null;
        }

        const config = draft[cell.dateStr] ?? getEffectiveConfig(role, cell.dateStr);
        const dayDefault = getDayOfWeekDefault(role, cell.date.getDay());

        return (
            <NeedsExceptionDayCell
                cell={cell}
                config={config}
                isException={!equalsDefaultOnEditable(config, dayDefault)}
                onChange={handleDayCellChange}
            />
        );
    }, [role, draft, handleDayCellChange]);

    const handleSave = async () => {
        if (!role || saving) {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const wgId = params.get("wgId") || "";
        const businessAccountId = params.get("bizId") || "";
        const snapshot = JSON.stringify(draft);
        const visit = visitRef.current;
        const stillOnSameRow = () => visitRef.current === visit;

        setSaving(true);
        setError(false);

        try {
            await updateExtendedWgData({
                wgId,
                businessAccountId,
                needsUpdate: {
                    templateId: "default",
                    roleId: role.roleId,
                    needId: role.needId,
                    apply: role.apply === "false" ? "false" : "true",
                    dicOfRoleSpecDaysConstraints: buildSpecDaysPayload(role, draft),
                },
            });

            if (stillOnSameRow()) {
                setCommittedJson(snapshot);
                setJustSaved(true);
            }
            onSaved?.();
        } catch (err) {
            console.error("Failed to save needs exceptions", err);
            if (stillOnSameRow()) {
                setError(true);
            }
        } finally {
            if (stillOnSameRow()) {
                setSaving(false);
            }
        }
    };

    let saveFeedback: { text: string; className: string } | null = null;
    if (error) {
        saveFeedback = { text: "Échec de l'enregistrement", className: "text-red-600" };
    } else if (justSaved && !hasUnsavedChanges) {
        saveFeedback = { text: "Enregistré", className: "" };
    }

    const headerActions = (
        <div className="relative flex flex-col items-center">
            <Button
                type="success"
                label="Enregistrer"
                rounded
                disabled={saving || !hasUnsavedChanges}
                onClick={handleSave}
                style={{ backgroundColor: "#16a34a" }}
            />
            {saveFeedback && (
                <span
                    className={`absolute top-full mt-2 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap ${saveFeedback.className}`}
                >
                    {saveFeedback.text}
                </span>
            )}
        </div>
    );

    return (
        <>
            <CalendarModalShell
                open={open}
                onClose={guarded(onClose)}
                title={role ? (role.needId ? `${role.label} — ${NEED_KEY_LABELS[role.needId] ?? role.needId}` : role.label) : ""}
                startDate={startDate}
                endDate={endDate}
                holidays={holidays}
                onPrev={onPrev ? guarded(onPrev) : undefined}
                onNext={onNext ? guarded(onNext) : undefined}
                hasPrev={hasPrev}
                hasNext={hasNext}
                headerActions={headerActions}
                renderDayCell={renderDayCell}
                strongGrid
            />
            <ConfirmModal
                open={!!pendingAction}
                onClose={() => setPendingAction(null)}
                onConfirm={() => pendingAction?.()}
                title="Modifications non enregistrées"
                message="Des modifications de cette tâche ne sont pas enregistrées et seront perdues. Continuer ?"
                confirmLabel="Quitter sans enregistrer"
                cancelLabel="Continuer l'édition"
                confirmVariant="danger"
            />
        </>
    );
}
